import React, { useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { BottomSheet } from "@/components/revanche/BottomSheet";
import { getPricing, getStripeConfig, checkoutEmbedded, creditsQuote, paymentStatus } from "@/lib/pay";

const eur = (v) => `${Number(v).toFixed(2).replace(".", ",")} €`;

// Achat de crédits sans quitter l'écran (le tarif unitaire n'apparaît jamais).
export const CreditSheet = ({ open, onClose, onPaid }) => {
    const [pricing, setPricing] = useState(null);
    const [stage, setStage] = useState("select"); // select | checkout | confirming
    const [selection, setSelection] = useState(null); // { lookup, credits }
    const [custom, setCustom] = useState("");
    const [customPrice, setCustomPrice] = useState(null);
    const [stripePromise, setStripePromise] = useState(null);
    const [error, setError] = useState("");
    const checkoutId = useRef(null);

    useEffect(() => {
        if (!open) return;
        setStage("select"); setSelection(null); setCustom(""); setCustomPrice(null); setError("");
        getPricing().then(setPricing).catch(() => {});
    }, [open]);

    // Prix total du champ libre, calculé côté serveur (jamais le prix unitaire).
    useEffect(() => {
        const n = parseInt(custom, 10);
        if (!n || n < 1) { setCustomPrice(null); return undefined; }
        const t = setTimeout(() => { creditsQuote(n).then((d) => setCustomPrice(d.price)).catch(() => {}); }, 250);
        return () => clearTimeout(t);
    }, [custom]);

    const startCheckout = async (lookup, credits) => {
        setError("");
        try {
            const cfg = await getStripeConfig();
            setStripePromise(loadStripe(cfg.publishable_key));
            setSelection({ lookup, credits });
            setStage("checkout");
        } catch (e) { setError("Paiement indisponible, réessaie."); }
    };

    const fetchClientSecret = useMemo(() => async () => {
        const d = await checkoutEmbedded(selection.lookup, selection.credits || null);
        checkoutId.current = d.session_id;
        return d.client_secret;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection]);

    const onComplete = async () => {
        setStage("confirming");
        for (let i = 0; i < 30; i += 1) {
            try {
                const s = await paymentStatus(checkoutId.current);
                if (s.payment_status === "paid") { onPaid && onPaid(); return; }
            } catch (e) { /* retry */ }
            await new Promise((r) => setTimeout(r, 1500));
        }
        setError("Paiement en cours de confirmation. Recharge la page dans un instant.");
    };

    const close = () => { if (stage !== "confirming") { setStage("select"); onClose(); } };

    const smallCr = pricing?.pack_small_credits ?? 20;
    const smallPr = pricing?.pack_small_price ?? 7.99;
    const bigCr = pricing?.pack_big_credits ?? 50;
    const bigPr = pricing?.pack_big_price ?? 9.99;

    return (
        <BottomSheet open={open} onClose={close} height={stage === "select" ? "auto" : "92dvh"} testId="credit-sheet">
            {stage === "select" && (
                <div className="px-6 pt-3 pb-6 flex flex-col" data-testid="credit-select">
                    <h2 className="font-body font-bold text-white" style={{ fontSize: 22 }}>Recharger des crédits</h2>

                    <div className="grid grid-cols-2 gap-3 mt-5">
                        <button data-testid="credit-pack-small" onClick={() => startCheckout("pack_small")}
                            className="tap-feedback rounded-2xl p-4 text-left"
                            style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
                            <div className="font-display text-white" style={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }}>{smallCr}</div>
                            <div className="font-body mt-1" style={{ fontSize: 13, color: "var(--text-secondary)" }}>crédits</div>
                            <div className="font-body text-white mt-3" style={{ fontSize: 16, fontWeight: 600 }}>{eur(smallPr)}</div>
                        </button>
                        <button data-testid="credit-pack-big" onClick={() => startCheckout("pack_big")}
                            className="tap-feedback rounded-2xl p-4 text-left relative"
                            style={{ background: "var(--surface-2)", border: "2px solid var(--red)" }}>
                            <span className="absolute -top-2 right-3 font-body" style={{ fontSize: 10, background: "var(--red)", color: "#fff", padding: "1px 6px", borderRadius: 4 }}>Meilleur deal</span>
                            <div className="font-display text-white" style={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }}>{bigCr}</div>
                            <div className="font-body mt-1" style={{ fontSize: 13, color: "var(--text-secondary)" }}>crédits</div>
                            <div className="font-body text-white mt-3" style={{ fontSize: 16, fontWeight: 600 }}>{eur(bigPr)}</div>
                        </button>
                    </div>

                    <div className="mt-4 rounded-2xl p-4" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
                        <p className="font-body" style={{ fontSize: 13, color: "var(--text-secondary)" }}>Ou choisis ton montant</p>
                        <div className="flex items-center gap-3 mt-2">
                            <input data-testid="credit-custom-input" type="number" inputMode="numeric" min="1"
                                value={custom} onChange={(e) => setCustom(e.target.value.replace(/[^0-9]/g, ""))}
                                placeholder="Nombre de crédits" style={{ fontSize: 16 }}
                                className="flex-1 bg-black rounded-xl px-4 h-12 text-white outline-none"
                                onWheel={(e) => e.target.blur()} />
                            <div className="font-display text-white text-right" data-testid="credit-custom-price" style={{ fontSize: 22, fontWeight: 800, minWidth: 80 }}>
                                {customPrice != null ? eur(customPrice) : "—"}
                            </div>
                        </div>
                        <button data-testid="credit-custom-pay" disabled={!customPrice}
                            onClick={() => startCheckout("pack_custom", parseInt(custom, 10))}
                            className="tap-feedback w-full mt-3 rounded-full font-body font-semibold text-white"
                            style={{ height: 52, background: "var(--red)", opacity: customPrice ? 1 : 0.4 }}>
                            {customPrice != null ? `Payer ${eur(customPrice)}` : "Payer"}
                        </button>
                    </div>
                    {error && <p className="font-body text-center mt-3" style={{ fontSize: 13, color: "var(--red)" }}>{error}</p>}
                </div>
            )}

            {stage !== "select" && (
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-none px-3 pt-2 pb-4 relative" data-testid="credit-checkout">
                    {stripePromise && stage === "checkout" && (
                        <div style={{ backgroundColor: "#FFFFFF", borderRadius: "var(--radius-card)", overflow: "hidden", minHeight: 400 }}>
                            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret, onComplete }}>
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                        </div>
                    )}
                    {stage === "confirming" && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4" data-testid="credit-confirming">
                            <div className="w-10 h-10 rounded-full border-2 border-ink-700 border-t-revred animate-spin" />
                            <p className="font-body text-white text-base">Confirmation du paiement…</p>
                        </div>
                    )}
                    {error && <p className="font-body text-center mt-3" style={{ fontSize: 13, color: "var(--red)" }}>{error}</p>}
                </div>
            )}
        </BottomSheet>
    );
};
